/**
 * What a move drawing under `public/moves/<id>.svg` must be before the app inlines it (ADR-058).
 *
 * The drawing is inlined rather than shown through `<img>` so its fill can be `currentColor` and
 * follow the theme (invariant 10), which means the file's markup lands in the page. The pipeline
 * (`scripts/gen-move-art.mjs`) only ever writes one path in one colour, so anything else — a
 * script, an event handler, an embedded raster, a fixed colour, an outside reference — is a file
 * that did not come from it, and the app refuses it rather than trusting the folder. The same
 * check is the test over `public/moves/` and the script's own gate before it ships a file.
 */

export const MOVE_ART_VIEWBOX = "0 0 1024 1024";

/** A drawing larger than this is not a line figure; the pipeline aims at 8 KB. */
export const MOVE_ART_MAX_BYTES = 24 * 1024;

const FORBIDDEN: readonly [RegExp, string][] = [
  [/<script/i, "a script"],
  [/<foreignObject/i, "a foreignObject"],
  [/<image/i, "an embedded image"],
  [/<style/i, "a style block"],
  [/data:/i, "a data URL"],
  [/\son[a-z]+\s*=/i, "an event handler"],
  [/(?:xlink:)?href\s*=/i, "a link to something outside the file"],
  [/url\s*\(/i, "a url() reference"],
  [/\sstyle\s*=/i, "an inline style"],
  [/#[0-9a-f]{3,8}\b/i, "a fixed colour"],
  [/\b(?:rgba?|hsla?)\s*\(/i, "a fixed colour"],
];

/** Every `fill`, `stroke` and `color` attribute must defer to the theme. */
const PAINT = /\s(fill|stroke|color|stop-color)\s*=\s*"([^"]*)"/gi;

/** Tags open and close in order, under one `<svg>` root — enough to call the file parseable. */
function balanced(text: string): boolean {
  const stack: string[] = [];
  let roots = 0;
  const tag = /<(\/?)([a-zA-Z][\w:-]*)([^>]*?)(\/?)>/g;
  const rest = text.replace(tag, (_m, close: string, name: string, _attrs: string, self: string) => {
    if (close) {
      if (stack.pop() !== name) roots = -99;
    } else {
      if (!stack.length) roots++;
      if (!self) stack.push(name);
    }
    return "";
  });
  return roots === 1 && !stack.length && !/[<>]/.test(rest);
}

/**
 * Why a drawing may not be shown, as words; an empty list means it may. Pure: the text in, the
 * reasons out.
 */
export function moveSvgProblems(text: string): string[] {
  const out: string[] = [];
  const t = text.trim();
  if (!t.startsWith("<svg")) return ["not an SVG"];
  if (!balanced(t)) out.push("not well-formed");
  if (new TextEncoder().encode(t).length > MOVE_ART_MAX_BYTES) out.push("too large");
  const root = /^<svg\b([^>]*)>/.exec(t)?.[1] ?? "";
  if (!root.includes(`viewBox="${MOVE_ART_VIEWBOX}"`)) out.push(`no viewBox of ${MOVE_ART_VIEWBOX}`);
  if (/\s(width|height)\s*=/.test(root)) out.push("a fixed width or height");
  for (const [re, why] of FORBIDDEN) if (re.test(t) && !out.includes(why)) out.push(why);
  for (const m of t.matchAll(PAINT)) {
    const v = m[2].trim();
    if (v !== "currentColor" && v !== "none" && !out.includes("a fixed colour")) out.push("a fixed colour");
  }
  return out;
}
